// FILE: src/components/EventsAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db, storage } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  updateDoc,
  serverTimestamp,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

import eventsService, {
  createEvent,
  getAllEvents,
  deleteEvent,
} from "../services/eventsService";

function safeUrl(value) {
  try {
    if (!value) return "";
    const u = new URL(value);
    return u.toString();
  } catch {
    return "";
  }
}

// Convert Firestore Timestamp/Date to datetime-local string
function toInputDateTime(ts) {
  if (!ts) return "";
  let d;
  if (ts?.seconds) d = new Date(ts.seconds * 1000);
  else if (ts?.toDate) d = ts.toDate();
  else d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

// Format Firestore timestamp for display
function formatDateTime(ts) {
  if (!ts) return "";
  if (ts?.seconds) return new Date(ts.seconds * 1000).toLocaleString();
  if (ts?.toDate) return ts.toDate().toLocaleString();
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return "";
  }
}

function csvEscape(value) {
  const s = String(value ?? "");
  const needsQuotes = /[",\n]/.test(s);
  const escaped = s.replace(/"/g, '""');
  return needsQuotes ? `"${escaped}"` : escaped;
}

function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const normalizeInt = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

// ✅ MUST match the member RSVP validation
function normalizeCodeId(code) {
  return String(code || "").trim().toUpperCase();
}

export default function EventsAdmin() {
  const [events, setEvents] = useState([]);

  const [form, setForm] = useState({
    title: "",
    description: "",
    startAt: "",
    endAt: "",
    location: "",
    isSundayService: false,
    isOnline: false,
    meetingUrl: "",
    posterUrl: "",
    posterPath: "",
    requireInviteCode: false,
  });

  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState("");

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [openDetailsId, setOpenDetailsId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  // RSVP modal
  const [rsvpEventId, setRsvpEventId] = useState(null);
  const [rsvpRows, setRsvpRows] = useState([]);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpError, setRsvpError] = useState("");

  // Invite Codes modal
  const [codesEventId, setCodesEventId] = useState(null);
  const [codesLoading, setCodesLoading] = useState(false);
  const [codesError, setCodesError] = useState("");
  const [inviteCodes, setInviteCodes] = useState([]);

  const [codeForm, setCodeForm] = useState({
    code: "",
    institution: "",
    maxUses: 0,
    enabled: true,
  });

  const [editingCodeId, setEditingCodeId] = useState(null);
  const [editCodeForm, setEditCodeForm] = useState({
    institution: "",
    maxUses: 0,
  });

  const isEditing = useMemo(() => !!editingId, [editingId]);

  const resetForm = () => {
    setForm({
      title: "",
      description: "",
      startAt: "",
      endAt: "",
      location: "",
      isSundayService: false,
      isOnline: false,
      meetingUrl: "",
      posterUrl: "",
      posterPath: "",
      requireInviteCode: false,
    });
    setPosterFile(null);
    setPosterPreview("");
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = getAllEvents ? await getAllEvents() : await eventsService.getAllEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load events:", err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onPosterPick = (file) => {
    setPosterFile(file || null);
    if (!file) {
      setPosterPreview("");
      return;
    }
    setPosterPreview(URL.createObjectURL(file));
  };

  const removePoster = async () => {
    if (!isEditing) {
      setForm((p) => ({ ...p, posterUrl: "", posterPath: "" }));
      setPosterFile(null);
      setPosterPreview("");
      return;
    }

    if (!window.confirm("Remove the poster from this event?")) return;

    setSubmitting(true);
    try {
      if (form.posterPath) {
        await deleteObject(storageRef(storage, form.posterPath));
      }

      const refDoc = doc(db, "events", editingId);
      await updateDoc(refDoc, {
        posterUrl: "",
        posterPath: "",
        updatedAt: serverTimestamp(),
      });

      setEvents((prev) =>
        prev.map((e) =>
          e.id === editingId ? { ...e, posterUrl: "", posterPath: "" } : e
        )
      );

      setForm((p) => ({ ...p, posterUrl: "", posterPath: "" }));
      setPosterFile(null);
      setPosterPreview("");
      alert("Poster removed.");
    } catch (err) {
      console.error("Remove poster failed:", err);
      alert("Failed to remove poster. See console.");
    } finally {
      setSubmitting(false);
    }
  };

  const uploadPosterIfNeeded = async (eventIdForPath) => {
    if (!posterFile) {
      return { posterUrl: form.posterUrl || "", posterPath: form.posterPath || "" };
    }
    if (!posterFile.type?.startsWith("image/")) {
      throw new Error("Poster must be an image file (png/jpg/webp).");
    }

    const ext = posterFile.name.split(".").pop() || "png";
    const path = `events/${eventIdForPath}/poster.${ext}`;
    const sRef = storageRef(storage, path);

    await uploadBytes(sRef, posterFile);
    const url = await getDownloadURL(sRef);

    return { posterUrl: url, posterPath: path };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title || !form.startAt) {
      alert("Title & Start time required");
      return;
    }

    if (form.isOnline && !safeUrl(form.meetingUrl)) {
      alert("Please enter a valid online event link (e.g. https://...).");
      return;
    }

    setSubmitting(true);

    try {
      const basePayload = {
        title: form.title,
        description: form.description || "",
        isSundayService: !!form.isSundayService,

        isOnline: !!form.isOnline,
        meetingUrl: form.isOnline ? safeUrl(form.meetingUrl) : "",
        location: form.isOnline ? "" : form.location || "",

        startAt: new Date(form.startAt),
        endAt: form.endAt ? new Date(form.endAt) : null,

        status: "Published",

        requireInviteCode: !!form.requireInviteCode,

        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        const { posterUrl, posterPath } = await uploadPosterIfNeeded(editingId);
        const payload = {
          ...basePayload,
          posterUrl: posterUrl || form.posterUrl || "",
          posterPath: posterPath || form.posterPath || "",
        };

        await updateDoc(doc(db, "events", editingId), payload);
        alert("Event updated.");
      } else {
        const created = createEvent
          ? await createEvent({ ...basePayload, createdAt: serverTimestamp() }, true)
          : await eventsService.createEvent({ ...basePayload, createdAt: serverTimestamp() }, true);

        const newId =
          typeof created === "string"
            ? created
            : created?.id
            ? created.id
            : created?.ref?.id
            ? created.ref.id
            : created?.docRef?.id
            ? created.docRef.id
            : null;

        if (!newId) {
          alert("Event created, but could not detect the event id. Please refresh and edit the event.");
          resetForm();
          await load();
          return;
        }

        const { posterUrl, posterPath } = await uploadPosterIfNeeded(newId);
        if (posterUrl) {
          await updateDoc(doc(db, "events", newId), {
            posterUrl,
            posterPath,
            updatedAt: serverTimestamp(),
          });
        }

        alert("Event created.");
      }

      resetForm();
      setEditingId(null);
      await load();
    } catch (err) {
      console.error("Save event failed:", err);
      alert("Failed to save event: " + (err?.message || String(err)));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (ev) => {
    if (!ev?.id) return alert("Delete failed: missing event id.");
    if (!window.confirm("Delete this event? This cannot be undone.")) return;

    try {
      if (ev.posterPath) {
        try {
          await deleteObject(storageRef(storage, ev.posterPath));
        } catch {}
      }

      if (deleteEvent) await deleteEvent(ev.id);
      else await eventsService.deleteEvent(ev.id);

      setEvents((prev) => prev.filter((e) => e.id !== ev.id));
      setOpenDetailsId((prev) => (prev === ev.id ? null : prev));
      alert("Event deleted.");
    } catch (err) {
      console.error("Delete event failed:", err);
      alert(err?.message || "Failed to delete event.");
    }
  };

  const handleEdit = (ev) => {
    setEditingId(ev.id);
    setPosterFile(null);
    setPosterPreview("");

    setForm({
      title: ev.title || "",
      description: ev.description || "",
      startAt: toInputDateTime(ev.startAt),
      endAt: toInputDateTime(ev.endAt),
      location: ev.location || "",
      isSundayService: !!ev.isSundayService,
      isOnline: !!ev.isOnline,
      meetingUrl: ev.meetingUrl || "",
      posterUrl: ev.posterUrl || "",
      posterPath: ev.posterPath || "",
      requireInviteCode: !!ev.requireInviteCode,
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    resetForm();
  };

  // RSVPs
  const openRsvps = async (eventId) => {
    setRsvpEventId(eventId);
    setRsvpRows([]);
    setRsvpError("");
    setRsvpLoading(true);

    try {
      const rsvpsCol = collection(db, "events", eventId, "rsvps");
      const q = query(rsvpsCol, orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setRsvpRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Failed to load RSVPs:", err);
      setRsvpError(err?.message || "Failed to load RSVPs");
    } finally {
      setRsvpLoading(false);
    }
  };

  const closeRsvps = () => {
    setRsvpEventId(null);
    setRsvpRows([]);
    setRsvpError("");
    setRsvpLoading(false);
  };

  const downloadRsvpsCsv = () => {
    if (!rsvpEventId) return;

    const header = ["uid", "name", "email", "phone", "status", "inviteCode", "institution", "createdAt"];
    const lines = [header.join(",")];

    rsvpRows.forEach((r) => {
      const created = r.createdAt?.toDate ? r.createdAt.toDate().toISOString() : "";
      lines.push(
        [
          csvEscape(r.uid || r.id || ""),
          csvEscape(r.name || r.fullName || ""),
          csvEscape(r.email || ""),
          csvEscape(r.phone || r.cellNumber || r.mobile || ""),
          csvEscape(r.status || ""),
          csvEscape(r.inviteCode || ""),
          csvEscape(r.institution || ""),
          csvEscape(created),
        ].join(",")
      );
    });

    downloadTextFile(`event-${rsvpEventId}-rsvps.csv`, lines.join("\n"));
  };

  const selectedEvent = useMemo(() => {
    if (!rsvpEventId) return null;
    return events.find((e) => e.id === rsvpEventId) || null;
  }, [events, rsvpEventId]);

  // Build actual usage counts from real RSVPs
  const getActualInviteCodeUsage = async (eventId) => {
    const usage = {};
    const rsvpsCol = collection(db, "events", eventId, "rsvps");
    const snap = await getDocs(rsvpsCol);

    snap.forEach((d) => {
      const data = d.data() || {};
      const code = normalizeCodeId(data.inviteCode || "");
      if (!code) return;
      usage[code] = (usage[code] || 0) + 1;
    });

    return usage;
  };

  // Invite Codes Manager
  const openInviteCodes = async (eventId) => {
    setCodesEventId(eventId);
    setInviteCodes([]);
    setCodesError("");
    setCodesLoading(true);
    setCodeForm({ code: "", institution: "", maxUses: 0, enabled: true });
    setEditingCodeId(null);
    setEditCodeForm({ institution: "", maxUses: 0 });

    try {
      const colRef = collection(db, "events", eventId, "inviteCodes");
      const q = query(colRef, orderBy("createdAt", "desc"));
      const [codesSnap, usageMap] = await Promise.all([
        getDocs(q),
        getActualInviteCodeUsage(eventId),
      ]);

      const rows = codesSnap.docs.map((d) => {
        const data = d.data() || {};
        const code = normalizeCodeId(data.code || d.id || "");
        const storedUsedCount = normalizeInt(data.usedCount, 0);
        const actualUsedCount = normalizeInt(usageMap[code], 0);
        const maxUses = normalizeInt(data.maxUses, 0);
        const remainingUses = Math.max(maxUses - actualUsedCount, 0);
        const isOverLimit = actualUsedCount > maxUses;
        const isAtLimit = actualUsedCount >= maxUses;

        return {
          id: d.id,
          ...data,
          code,
          storedUsedCount,
          actualUsedCount,
          maxUses,
          remainingUses,
          isOverLimit,
          isAtLimit,
          hasUsageMismatch: storedUsedCount !== actualUsedCount,
        };
      });

      rows.sort((a, b) => {
        if (a.isOverLimit !== b.isOverLimit) return a.isOverLimit ? -1 : 1;
        if (a.hasUsageMismatch !== b.hasUsageMismatch) return a.hasUsageMismatch ? -1 : 1;
        return String(a.code).localeCompare(String(b.code));
      });

      setInviteCodes(rows);
    } catch (err) {
      console.error("Failed to load invite codes:", err);
      setCodesError(err?.message || "Failed to load invite codes");
    } finally {
      setCodesLoading(false);
    }
  };

  const closeInviteCodes = () => {
    setCodesEventId(null);
    setInviteCodes([]);
    setCodesError("");
    setCodesLoading(false);
    setEditingCodeId(null);
    setEditCodeForm({ institution: "", maxUses: 0 });
  };

  // ✅ create invite code with DOC ID = CODE (uppercase)
  const addInviteCode = async () => {
    if (!codesEventId) return;

    const rawCode = String(codeForm.code || "").trim();
    const codeId = normalizeCodeId(rawCode);
    const institution = String(codeForm.institution || "").trim();
    const maxUses = normalizeInt(codeForm.maxUses, 0);

    if (!rawCode) return alert("Please enter a code.");
    if (!institution) return alert("Please enter an institution.");
    if (!Number.isFinite(maxUses) || maxUses <= 0) return alert("Max uses must be a number > 0.");

    setCodesLoading(true);
    try {
      const codeRef = doc(db, "events", codesEventId, "inviteCodes", codeId);

      const existing = await getDoc(codeRef);
      if (existing.exists()) {
        alert("That code already exists for this event.");
        return;
      }

      await setDoc(codeRef, {
        code: codeId,
        codeLower: codeId.toLowerCase(),
        institution,
        maxUses,
        usedCount: 0,
        enabled: !!codeForm.enabled,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await openInviteCodes(codesEventId);
      setCodeForm({ code: "", institution: "", maxUses: 0, enabled: true });
    } catch (err) {
      console.error("Add invite code failed:", err);
      alert("Failed to add invite code: " + (err?.message || String(err)));
    } finally {
      setCodesLoading(false);
    }
  };

  const startEditInviteCode = (codeRow) => {
    setEditingCodeId(codeRow.id);
    setEditCodeForm({
      institution: codeRow.institution || "",
      maxUses: normalizeInt(codeRow.maxUses, 0),
    });
  };

  const cancelEditInviteCode = () => {
    setEditingCodeId(null);
    setEditCodeForm({ institution: "", maxUses: 0 });
  };

  const saveInviteCodeEdit = async (codeRow) => {
    if (!codesEventId || !codeRow?.id) return;

    const institution = String(editCodeForm.institution || "").trim();
    const maxUses = normalizeInt(editCodeForm.maxUses, 0);
    const actualUsedCount = normalizeInt(codeRow.actualUsedCount, 0);

    if (!institution) {
      alert("Please enter an institution.");
      return;
    }

    if (!Number.isFinite(maxUses) || maxUses <= 0) {
      alert("Max uses must be a number greater than 0.");
      return;
    }

    if (maxUses < actualUsedCount) {
      alert(`Max uses cannot be less than actual RSVPs (${actualUsedCount}).`);
      return;
    }

    try {
      const refDoc = doc(db, "events", codesEventId, "inviteCodes", codeRow.id);

      await updateDoc(refDoc, {
        institution,
        maxUses,
        updatedAt: serverTimestamp(),
      });

      await openInviteCodes(codesEventId);

      setEditingCodeId(null);
      setEditCodeForm({ institution: "", maxUses: 0 });
      alert("Invite code updated.");
    } catch (err) {
      console.error("Update invite code failed:", err);
      alert("Failed to update code: " + (err?.message || String(err)));
    }
  };

  const syncInviteCodeUsage = async (codeRow) => {
    if (!codesEventId || !codeRow?.id) return;
    try {
      const refDoc = doc(db, "events", codesEventId, "inviteCodes", codeRow.id);
      await updateDoc(refDoc, {
        usedCount: normalizeInt(codeRow.actualUsedCount, 0),
        updatedAt: serverTimestamp(),
      });
      await openInviteCodes(codesEventId);
      alert(`Usage synced for code ${codeRow.code}.`);
    } catch (err) {
      console.error("Sync invite code usage failed:", err);
      alert("Failed to sync code usage: " + (err?.message || String(err)));
    }
  };

  const syncAllInviteCodeUsage = async () => {
    if (!codesEventId || inviteCodes.length === 0) return;

    setCodesLoading(true);
    try {
      await Promise.all(
        inviteCodes.map((c) =>
          updateDoc(doc(db, "events", codesEventId, "inviteCodes", c.id), {
            usedCount: normalizeInt(c.actualUsedCount, 0),
            updatedAt: serverTimestamp(),
          })
        )
      );

      await openInviteCodes(codesEventId);
      alert("All invite code usage counts synced.");
    } catch (err) {
      console.error("Sync all invite code usage failed:", err);
      alert("Failed to sync all code usage: " + (err?.message || String(err)));
    } finally {
      setCodesLoading(false);
    }
  };

  const toggleInviteCodeEnabled = async (codeRow) => {
    if (!codesEventId || !codeRow?.id) return;
    try {
      const refDoc = doc(db, "events", codesEventId, "inviteCodes", codeRow.id);
      await updateDoc(refDoc, { enabled: !codeRow.enabled, updatedAt: serverTimestamp() });
      await openInviteCodes(codesEventId);
    } catch (err) {
      console.error("Toggle invite code failed:", err);
      alert("Failed to update code: " + (err?.message || String(err)));
    }
  };

  const deleteInviteCode = async (codeRow) => {
    if (!codesEventId || !codeRow?.id) return;
    if (!window.confirm("Delete this invite code?")) return;

    try {
      const refDoc = doc(db, "events", codesEventId, "inviteCodes", codeRow.id);
      await deleteDoc(refDoc);
      setInviteCodes((prev) => prev.filter((c) => c.id !== codeRow.id));

      if (editingCodeId === codeRow.id) {
        cancelEditInviteCode();
      }
    } catch (err) {
      console.error("Delete invite code failed:", err);
      alert("Failed to delete code: " + (err?.message || String(err)));
    }
  };

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-6">
      <h2 className="text-2xl font-bold">Admin: Manage Events</h2>

      {/* Create / Edit Form */}
      <form onSubmit={handleSubmit} className="space-y-4 mb-4 p-4 border rounded-xl bg-white shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            placeholder="Event Title"
            className="w-full p-2 border rounded"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <input
            placeholder="Location (physical only)"
            className="w-full p-2 border rounded"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            disabled={form.isOnline}
          />
          <input
            type="datetime-local"
            className="w-full p-2 border rounded"
            value={form.startAt}
            onChange={(e) => setForm({ ...form, startAt: e.target.value })}
          />
          <input
            type="datetime-local"
            className="w-full p-2 border rounded"
            value={form.endAt}
            onChange={(e) => setForm({ ...form, endAt: e.target.value })}
          />
        </div>

        <textarea
          placeholder="Description"
          className="w-full p-2 border rounded"
          rows={3}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />

        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isSundayService}
              onChange={(e) => setForm({ ...form, isSundayService: e.target.checked })}
            />
            Sunday Service
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isOnline}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  isOnline: e.target.checked,
                  location: e.target.checked ? "" : p.location,
                }))
              }
            />
            Online event
          </label>

          {form.isOnline && (
            <input
              placeholder="Online link (https://...)"
              className="flex-1 min-w-[240px] p-2 border rounded"
              value={form.meetingUrl}
              onChange={(e) => setForm({ ...form, meetingUrl: e.target.value })}
            />
          )}
        </div>

        {/* Invite system */}
        <div className="flex flex-wrap items-center gap-4 border-t pt-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.requireInviteCode}
              onChange={(e) => setForm((p) => ({ ...p, requireInviteCode: e.target.checked }))}
            />
            Require invite code for RSVP
          </label>

          {editingId && form.requireInviteCode && (
            <button
              type="button"
              onClick={() => openInviteCodes(editingId)}
              className="px-3 py-1 rounded border text-sm bg-amber-50 text-amber-800"
            >
              Manage invite codes
            </button>
          )}

          {!editingId && form.requireInviteCode && (
            <div className="text-xs text-gray-600">
              Create the event first, then click “Manage invite codes”.
            </div>
          )}
        </div>

        {/* Poster upload */}
        <div className="border-t pt-4 space-y-2">
          <div className="text-sm font-semibold">Poster (optional)</div>

          {form.posterUrl && !posterPreview && (
            <div className="space-y-2">
              <img src={form.posterUrl} alt="Current poster" className="w-full max-h-64 object-contain border rounded bg-white" />
              <button type="button" onClick={removePoster} disabled={submitting} className="px-3 py-1 rounded border text-sm">
                Remove poster
              </button>
            </div>
          )}

          {posterPreview && (
            <img src={posterPreview} alt="New poster preview" className="w-full max-h-64 object-contain border rounded bg-white" />
          )}

          <input type="file" accept="image/*" onChange={(e) => onPosterPick(e.target.files?.[0] || null)} />
        </div>

        <div className="flex gap-2">
          <button type="submit" disabled={submitting} className="px-4 py-2 rounded bg-indigo-600 text-white text-sm disabled:opacity-60">
            {editingId ? "Update Event" : "Create Event"}
          </button>

          {editingId && (
            <button type="button" onClick={handleCancelEdit} disabled={submitting} className="px-4 py-2 rounded border text-sm disabled:opacity-60">
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Events list */}
      <div className="space-y-3">
        {loading ? (
          <p className="text-sm text-gray-600">Loading events…</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-gray-600">No events yet.</p>
        ) : (
          events
            .slice()
            .sort((a, b) => {
              const da = a.startAt?.seconds ? a.startAt.seconds : 0;
              const dbb = b.startAt?.seconds ? b.startAt.seconds : 0;
              return dbb - da;
            })
            .map((ev) => {
              const isOpen = openDetailsId === ev.id;
              return (
                <div key={ev.id} className="border rounded-xl bg-white shadow-sm p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-semibold text-lg truncate">{ev.title || "Event"}</div>
                        {ev.isOnline && <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] rounded-full">Online</span>}
                        {ev.requireInviteCode && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-full">Invite codes</span>}
                      </div>

                      <div className="text-xs text-gray-600 mt-1">{ev.startAt ? formatDateTime(ev.startAt) : "No date"}</div>

                      {!ev.isOnline && ev.location && <div className="text-xs text-gray-500 mt-1">📍 {ev.location}</div>}

                      {ev.isOnline && ev.meetingUrl && (
                        <a className="text-xs text-indigo-600 underline mt-1 inline-block" href={safeUrl(ev.meetingUrl)} target="_blank" rel="noreferrer">
                          Online link
                        </a>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <button type="button" onClick={() => setOpenDetailsId(isOpen ? null : ev.id)} className="px-3 py-1 rounded border text-sm">
                        {isOpen ? "Hide" : "Details"}
                      </button>

                      <button type="button" onClick={() => handleEdit(ev)} className="px-3 py-1 rounded border text-sm">
                        Edit
                      </button>

                      {ev.requireInviteCode && (
                        <button
                          type="button"
                          onClick={() => openInviteCodes(ev.id)}
                          className="px-3 py-1 rounded border text-sm bg-amber-50 text-amber-800"
                        >
                          Invite codes
                        </button>
                      )}

                      <button type="button" onClick={() => openRsvps(ev.id)} className="px-3 py-1 rounded border text-sm bg-indigo-50 text-indigo-700">
                        View RSVPs
                      </button>

                      <button type="button" onClick={() => handleDelete(ev)} className="px-3 py-1 rounded border text-sm text-red-600">
                        Delete
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-3 text-sm text-gray-700 space-y-2">
                      {ev.posterUrl && <img src={ev.posterUrl} alt="Poster" className="w-full max-h-72 object-contain border rounded bg-white" />}
                      {ev.description && <div>{ev.description}</div>}
                    </div>
                  )}
                </div>
              );
            })
        )}
      </div>

      {/* RSVP Modal */}
      {rsvpEventId && (
        <div className="fixed inset-0 z-40 bg-black/40 overflow-y-auto">
          <div className="min-h-full flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-4xl rounded-2xl shadow-lg max-h-[90vh] flex flex-col overflow-hidden">
              <div className="flex items-start justify-between gap-3 p-5 border-b">
                <div className="min-w-0">
                  <div className="text-lg font-bold">
                    RSVPs{selectedEvent?.title ? `: ${selectedEvent.title}` : ""}
                  </div>
                  <div className="text-xs text-gray-600">
                    {selectedEvent?.startAt ? formatDateTime(selectedEvent.startAt) : ""}
                  </div>
                </div>

                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={downloadRsvpsCsv}
                    className="px-3 py-1 rounded border text-sm bg-green-50 text-green-700"
                    disabled={rsvpLoading || rsvpRows.length === 0}
                  >
                    Download CSV
                  </button>

                  <button type="button" onClick={closeRsvps} className="px-3 py-1 rounded border text-sm">
                    Close
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5">
                {rsvpLoading ? (
                  <p className="text-sm text-gray-600">Loading RSVPs…</p>
                ) : rsvpError ? (
                  <p className="text-sm text-red-600">{rsvpError}</p>
                ) : rsvpRows.length === 0 ? (
                  <p className="text-sm text-gray-600">No RSVPs yet.</p>
                ) : (
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr className="text-left">
                          <th className="p-2 border-b">Name</th>
                          <th className="p-2 border-b">Email</th>
                          <th className="p-2 border-b">Cell Number</th>
                          <th className="p-2 border-b">Status</th>
                          <th className="p-2 border-b">Code</th>
                          <th className="p-2 border-b">Institution</th>
                          <th className="p-2 border-b">RSVP Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rsvpRows.map((r) => (
                          <tr key={r.id} className="hover:bg-gray-50">
                            <td className="p-2 border-b">{r.name || r.fullName || ""}</td>
                            <td className="p-2 border-b">{r.email || ""}</td>
                            <td className="p-2 border-b">{r.phone || r.cellNumber || r.mobile || "—"}</td>
                            <td className="p-2 border-b">{r.status || ""}</td>
                            <td className="p-2 border-b">{r.inviteCode || ""}</td>
                            <td className="p-2 border-b">{r.institution || ""}</td>
                            <td className="p-2 border-b">
                              {r.createdAt?.toDate ? r.createdAt.toDate().toLocaleString() : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="mt-3 text-xs text-gray-500">
                  CSV includes: uid, name, email, phone, status, inviteCode, institution, createdAt
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Codes Modal */}
      {codesEventId && (
        <div className="fixed inset-0 z-50 bg-black/40 overflow-y-auto">
          <div className="min-h-full flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-6xl rounded-2xl shadow-lg max-h-[90vh] flex flex-col overflow-hidden">
              <div className="flex items-start justify-between gap-3 p-5 border-b">
                <div className="min-w-0">
                  <div className="text-lg font-bold">Invite Codes</div>
                  <div className="text-xs text-gray-600">Event ID: {codesEventId}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    IMPORTANT: Code docs are stored with DOC ID = CODE (uppercase).
                  </div>
                </div>

                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={syncAllInviteCodeUsage}
                    disabled={codesLoading || inviteCodes.length === 0}
                    className="px-3 py-1 rounded border text-sm bg-blue-50 text-blue-700 disabled:opacity-60"
                  >
                    Sync all usage
                  </button>

                  <button type="button" onClick={closeInviteCodes} className="px-3 py-1 rounded border text-sm">
                    Close
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5">
                <div className="grid gap-3 md:grid-cols-4">
                  <input
                    className="p-2 border rounded"
                    placeholder="Code (e.g. CMP-STAFF-8)"
                    value={codeForm.code}
                    onChange={(e) => setCodeForm((p) => ({ ...p, code: e.target.value }))}
                  />
                  <input
                    className="p-2 border rounded"
                    placeholder="Institution (e.g. STAFF)"
                    value={codeForm.institution}
                    onChange={(e) => setCodeForm((p) => ({ ...p, institution: e.target.value }))}
                  />
                  <input
                    className="p-2 border rounded"
                    type="number"
                    placeholder="Max uses"
                    value={codeForm.maxUses}
                    onChange={(e) => setCodeForm((p) => ({ ...p, maxUses: e.target.value }))}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={addInviteCode}
                      disabled={codesLoading}
                      className="flex-1 px-3 py-2 rounded bg-indigo-600 text-white text-sm disabled:opacity-60"
                    >
                      Add code
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  {codesLoading ? (
                    <p className="text-sm text-gray-600">Loading codes…</p>
                  ) : codesError ? (
                    <p className="text-sm text-red-600">{codesError}</p>
                  ) : inviteCodes.length === 0 ? (
                    <p className="text-sm text-gray-600">No invite codes yet.</p>
                  ) : (
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr className="text-left">
                            <th className="p-2 border-b">Code</th>
                            <th className="p-2 border-b">Institution</th>
                            <th className="p-2 border-b">Stored Used</th>
                            <th className="p-2 border-b">Actual RSVPs</th>
                            <th className="p-2 border-b">Max</th>
                            <th className="p-2 border-b">Remaining</th>
                            <th className="p-2 border-b">Enabled</th>
                            <th className="p-2 border-b">Status</th>
                            <th className="p-2 border-b">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inviteCodes.map((c) => {
                            const isRowEditing = editingCodeId === c.id;

                            return (
                              <tr key={c.id} className={c.isOverLimit ? "bg-red-50" : "hover:bg-gray-50"}>
                                <td className="p-2 border-b font-medium">{c.code}</td>

                                <td className="p-2 border-b">
                                  {isRowEditing ? (
                                    <input
                                      className="w-full p-2 border rounded"
                                      value={editCodeForm.institution}
                                      onChange={(e) =>
                                        setEditCodeForm((prev) => ({
                                          ...prev,
                                          institution: e.target.value,
                                        }))
                                      }
                                    />
                                  ) : (
                                    c.institution
                                  )}
                                </td>

                                <td className="p-2 border-b">{normalizeInt(c.storedUsedCount, 0)}</td>

                                <td className="p-2 border-b font-semibold">
                                  {normalizeInt(c.actualUsedCount, 0)}
                                </td>

                                <td className="p-2 border-b">
                                  {isRowEditing ? (
                                    <input
                                      className="w-24 p-2 border rounded"
                                      type="number"
                                      min={normalizeInt(c.actualUsedCount, 0)}
                                      value={editCodeForm.maxUses}
                                      onChange={(e) =>
                                        setEditCodeForm((prev) => ({
                                          ...prev,
                                          maxUses: e.target.value,
                                        }))
                                      }
                                    />
                                  ) : (
                                    normalizeInt(c.maxUses, 0)
                                  )}
                                </td>

                                <td className="p-2 border-b">
                                  {normalizeInt(c.remainingUses, 0)}
                                </td>

                                <td className="p-2 border-b">{c.enabled ? "Yes" : "No"}</td>

                                <td className="p-2 border-b">
                                  <div className="flex flex-wrap gap-1">
                                    {c.isOverLimit && (
                                      <span className="px-2 py-0.5 text-[10px] rounded-full bg-red-100 text-red-700">
                                        Over limit
                                      </span>
                                    )}
                                    {!c.isOverLimit && c.isAtLimit && (
                                      <span className="px-2 py-0.5 text-[10px] rounded-full bg-amber-100 text-amber-700">
                                        Full
                                      </span>
                                    )}
                                    {c.hasUsageMismatch && (
                                      <span className="px-2 py-0.5 text-[10px] rounded-full bg-blue-100 text-blue-700">
                                        Count mismatch
                                      </span>
                                    )}
                                    {!c.isOverLimit && !c.isAtLimit && !c.hasUsageMismatch && (
                                      <span className="px-2 py-0.5 text-[10px] rounded-full bg-green-100 text-green-700">
                                        OK
                                      </span>
                                    )}
                                  </div>
                                </td>

                                <td className="p-2 border-b">
                                  <div className="flex flex-wrap gap-2">
                                    {isRowEditing ? (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => saveInviteCodeEdit(c)}
                                          className="px-3 py-1 rounded border text-xs bg-green-50 text-green-700"
                                        >
                                          Save
                                        </button>
                                        <button
                                          type="button"
                                          onClick={cancelEditInviteCode}
                                          className="px-3 py-1 rounded border text-xs"
                                        >
                                          Cancel
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => startEditInviteCode(c)}
                                          className="px-3 py-1 rounded border text-xs bg-blue-50 text-blue-700"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => syncInviteCodeUsage(c)}
                                          className="px-3 py-1 rounded border text-xs bg-slate-50 text-slate-700"
                                        >
                                          Sync used
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => toggleInviteCodeEnabled(c)}
                                          className="px-3 py-1 rounded border text-xs"
                                        >
                                          {c.enabled ? "Disable" : "Enable"}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => deleteInviteCode(c)}
                                          className="px-3 py-1 rounded border text-xs text-red-600"
                                        >
                                          Delete
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="mt-3 text-xs text-gray-500">
                  Stored Used = value saved on the invite code document. Actual RSVPs = live count from event RSVPs. If they differ, click “Sync used”. If Actual RSVPs is greater than Max, that means the RSVP flow elsewhere allowed overbooking and must also be fixed.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}